import { forwardRef } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import get from 'lodash/fp/get';

import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';

import Image from 'next/image';

import Button from 'Components/LegacyButton';
import Spacer from 'Components/Spacer';
import Typography from 'Components/Typography';

import ConsultationCTA from 'Containers/ConsultationCTA';

import { heroContentSkincare as heroContentDI } from 'assets/content/home';
import playOutline from 'assets/images/play-outline.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import * as userSelectors from 'dux/user/selectors';

const Section = styled.section`
  ${({ theme }) => `
    display: grid;
    grid-template-areas: "hero";

    min-height: calc(100vh - ${theme.props.headerHeight}px);
    width: 100%;
  `}
`;

const Content = styled.div`
  ${({ theme }) => `
  grid-area: hero;

  width: 100%;
  max-width: 100vw;

  padding-top: ${theme.spacing.s48}px;
  padding-left: ${theme.spacing.s24}px;
  padding-right: ${theme.spacing.s24}px;


  ${theme.breakpoints.up('sm')} {
    display: flex;
    flex-flow: column nowrap;
  }

  ${theme.breakpoints.up('md')} {
    justify-content: center;
    padding: 0 0 0 58%;
  }
  `}
`;

const BackgroundImage = styled(Image, { shouldForwardProp: isPropValid })`
  grid-area: hero;

  object-fit: cover;
  ${({ imgposition }) => (imgposition ? `object-position: ${imgposition};` : '')}
  height: calc(100vh - ${({ theme }) => theme.props.headerHeight}px);
  width: 100%;
`;

const Title = styled(Typography)`
  ${({ theme }) => `
    font-size: 34px;
    max-width: 550px;

    ${theme.breakpoints.match('sm')} {
        max-width: 290px;
    }

    ${theme.breakpoints.up('xs')} {
        font-size: 28px;
    }
    ${theme.breakpoints.up('sm')} {
        font-size: 48px;
    }
    ${theme.breakpoints.up('md')} {
      max-width: 390px;
    }
  `}
`;

const SubTitle = styled(Typography)`
  max-width: 290px;

  ${({ theme }) => `
    ${theme.breakpoints.match('xs')} {
      max-width: 260px;
    }
    ${theme.breakpoints.match('sm')} {
      max-width: 260px;
    }

  `}
`;

const HowItWorksButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HowItWorksButtonText = styled(Typography)`
  margin-bottom: -5px;
`;

const HomeHeroSkincare = forwardRef(
  (
    {
      heroContent,
      showCTA,
      hasCompletedHaircareConsultation,
      hasCompletedSkincareConsultation,
      hasOrdersByCategory,
      onOpenVideo,
    },
    ref
  ) => {
    const contentPath = () => {
      if (hasCompletedHaircareConsultation && !hasCompletedSkincareConsultation) {
        return 'hasHaircareConsultationComplete';
      }
      if (!hasCompletedHaircareConsultation && hasCompletedSkincareConsultation) {
        return 'hasSkincareConsultationComplete';
      }
      return 'isDefault';
    };

    const {
      title,
      subtitle,
      cta,
      backgroundMobile,
      backgroundTablet,
      backgroundDesktop,
      backgroundDesktopObjectPosition,
      imageAlt,
    } = get(contentPath())(heroContent);

    const { isMobile, isTablet, isDesktop } = useResponsiveVariant();

    return (
      <Section ref={ref} data-testid="home-hero">
        {isMobile && (
          <BackgroundImage
            alt={imageAlt}
            data-testid={`hp-hero-img-${contentPath()}`}
            height="1920"
            priority
            src={backgroundMobile.src}
            width="1080"
          />
        )}
        {isTablet && (
          <BackgroundImage
            alt={imageAlt}
            data-testid={`hp-hero-img-${contentPath()}`}
            height="1600"
            priority
            src={backgroundTablet.src}
            width="1200"
          />
        )}
        {isDesktop && (
          <BackgroundImage
            alt={imageAlt}
            data-testid={`hp-hero-img-${contentPath()}`}
            imgposition={backgroundDesktopObjectPosition}
            priority
            src={backgroundDesktop}
          />
        )}
        <Content>
          <Title data-testid="home-hero-title" variant="hero2">
            {title}
          </Title>
          <Spacer size={isMobile ? 16 : 24} />
          {subtitle && <SubTitle variant={isMobile ? 'p2' : 'p1'}>{subtitle}</SubTitle>}
          <Spacer size={isMobile ? 16 : 24} />
          {showCTA && cta.component === 'Cta' && (
            <ConsultationCTA
              css={{
                width: '100%',
                maxWidth: '279px',
              }}
              data-id="home-hero-cta"
              data-testid="home-hero-cta"
              dataFrom="homepage-hero"
              noMargin
              variant="vertFull"
            />
          )}
          {!hasOrdersByCategory.haircare && (
            <>
              <Spacer size={8} />
              <Button
                css={{
                  minWidth: '279px',
                  maxWidth: '279px',
                }}
                data-click="see-how-it-works"
                data-from="homepage-hero"
                data-testid="see-how-it-works-button"
                noMargin
                onClick={onOpenVideo}
                variant="whiteBgOutline"
              >
                <HowItWorksButtonWrapper>
                  <HowItWorksButtonText>See how it works</HowItWorksButtonText>
                  <Spacer size={16} />
                  <Image alt="Play icon" height={20} src={playOutline} width={20} />
                </HowItWorksButtonWrapper>
              </Button>
            </>
          )}
        </Content>
      </Section>
    );
  }
);

HomeHeroSkincare.defaultProps = {
  showCTA: true,
  heroContent: heroContentDI,
  hasCompletedHaircareConsultation: false,
  hasCompletedSkincareConsultation: false,
  onOpenVideo: null,
};

HomeHeroSkincare.propTypes = {
  showCTA: PropTypes.bool,
  heroContent: PropTypes.shape({}),
  hasCompletedHaircareConsultation: PropTypes.bool,
  hasCompletedSkincareConsultation: PropTypes.bool,
  hasOrdersByCategory: PropTypes.shape({
    all: PropTypes.bool,
    oneOf: PropTypes.bool,
    haircare: PropTypes.bool,
    skincare: PropTypes.bool,
  }).isRequired,
  onOpenVideo: PropTypes.func,
};

HomeHeroSkincare.displayName = 'HomeHeroSkincare';

export default connect(
  state => ({
    hasCompletedHaircareConsultation: userSelectors.getHasCompletedHaircareConsultation(state),
    hasCompletedSkincareConsultation: userSelectors.getHasCompletedSkincareConsultation(state),
    hasOrdersByCategory: userSelectors.getHasOrdersByCategory(state),
  }),
  null,
  null,
  { forwardRef: true }
)(HomeHeroSkincare);
